import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./EmbedBuilder.css";

const DEFAULT_EMBED = {
  enabled: false,
  color: "#5865F2",
  author_name: "",
  author_url: "",
  author_icon_url: "",
  title: "",
  title_url: "",
  description: "",
  fields: [],
  image_url: "",
  thumbnail_url: "",
  footer_text: "",
  footer_icon_url: "",
};

/**
 * EmbedBuilder – editor de mensagem com suporte ao embed do Discord.
 *
 * Props:
 *   value    – objeto { content: string, embed: object|null }
 *   onChange – callback(newValue)
 */
function EmbedBuilder({ value = {}, onChange }) {
  const { t } = useTranslation();
  const content = value.content ?? "";
  // Merge com DEFAULT_EMBED para garantir que todas as propriedades existam
  // (ex: quando o banco salva apenas { enabled: false }, 'fields' estava undefined)
  const embed = { ...DEFAULT_EMBED, ...(value.embed ?? {}) };

  const [activeTab, setActiveTab] = useState("editor"); // 'editor' | 'preview'

  const update = useCallback((patch) => onChange({ content, embed, ...patch }), [content, embed, onChange]);

  const updateEmbed = useCallback((patch) => onChange({ content, embed: { ...embed, ...patch } }), [content, embed, onChange]);

  /* ── Helpers para campos de Fields do Embed ── */
  const addField = () =>
    updateEmbed({
      fields: [...embed.fields, { name: "", value: "", inline: false }],
    });

  const updateField = (i, patch) => {
    const fields = embed.fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f));
    updateEmbed({ fields });
  };

  const removeField = (i) => updateEmbed({ fields: embed.fields.filter((_, idx) => idx !== i) });

  /* ── Preview: substitui placeholders por valores visuais ── */
  const preview = (text = "") =>
    text
      // ── Boas-vindas / Saída ─────────────────────────────────────────────
      .replace(/\{@user\}/g, "@Membro")
      .replace(/\{user\.name\}/g, "Membro")
      .replace(/\{user\}/g, "Membro")
      .replace(/\{user\.avatar\}/g, "https://cdn.discordapp.com/embed/avatars/0.png")
      .replace(/\{user\.id\}/g, "000000000000000000")
      .replace(/\{user\.discriminator\}/g, "0000")
      .replace(/\{user\.tag\}/g, "@membro")
      .replace(/\{guild\.name\}/g, "Meu Servidor")
      .replace(/\{guild\}/g, "Meu Servidor")
      .replace(/\{server\}/g, "Meu Servidor")
      .replace(/\{guild\.size\}/g, "102")
      .replace(/\{memberCount\}/g, "102")
      .replace(/\{guild\.icon\}/g, "https://cdn.discordapp.com/embed/avatars/0.png")
      // ── Logs de moderação — alvo ────────────────────────────────────────
      .replace(/\{target\.name\}/g, "Fulano")
      .replace(/\{target\.id\}/g, "111111111111111111")
      .replace(/\{target\.tag\}/g, "@Fulano")
      // ── Logs de moderação — moderador ───────────────────────────────────
      .replace(/\{moderator\.name\}/g, "Moderador")
      .replace(/\{moderator\.id\}/g, "222222222222222222")
      .replace(/\{moderator\.tag\}/g, "@Moderador")
      // ── Logs de moderação — extras ──────────────────────────────────────
      .replace(/\{reason\}/g, "Comportamento inadequado")
      .replace(/\{duration\}/g, "10min")
      // ── Logs de mensagem ────────────────────────────────────────────────
      .replace(/\{author\.name\}/g, "Autor")
      .replace(/\{author\.id\}/g, "333333333333333333")
      .replace(/\{author\.tag\}/g, "@Autor")
      .replace(/\{channel\}/g, "#geral")
      .replace(/\{content\}/g, "Mensagem de exemplo apagada")
      .replace(/\{old_content\}/g, "Texto original da mensagem")
      .replace(/\{new_content\}/g, "Texto editado da mensagem")
      // ── Logs de cargo ────────────────────────────────────────────────────
      .replace(/\{role\.name\}/g, "Membro VIP")
      .replace(/\{role\.id\}/g, "444444444444444444");

  return (
    <div className="embed-builder">
      {/* Abas */}
      <div className="eb-tabs">
        <button className={`eb-tab ${activeTab === "editor" ? "active" : ""}`} onClick={() => setActiveTab("editor")}>
          {t("embed_builder.tabs.editor")}
        </button>
        <button className={`eb-tab ${activeTab === "preview" ? "active" : ""}`} onClick={() => setActiveTab("preview")}>
          {t("embed_builder.tabs.preview")}
        </button>
      </div>

      <div className="eb-body">
        {/* ═══════════════ EDITOR ═══════════════ */}
        {activeTab === "editor" && (
          <div className="eb-editor">
            {/* Conteúdo de texto */}
            <div className="eb-section">
              <label className="eb-label">{t("embed_builder.labels.message_content")}</label>
              <textarea
                className="eb-textarea"
                rows={3}
                placeholder={t("embed_builder.placeholders.content")}
                value={content}
                onChange={(e) => update({ content: e.target.value })}
              />
            </div>

            {/* Toggle do Embed */}
            <div className="eb-toggle-bar">
              <span className="eb-label">{t("embed_builder.labels.add_embed")}</span>
              <div className="toggle-switch">
                <input type="checkbox" id="embed-toggle" checked={embed.enabled} onChange={(e) => updateEmbed({ enabled: e.target.checked })} />
                <label className="toggle-slider" htmlFor="embed-toggle" />
              </div>
            </div>

            {/* Campos do Embed */}
            {embed.enabled && (
              <div className="eb-embed-fields">
                {/* Cor */}
                <div className="eb-field-row">
                  <label className="eb-label">{t("embed_builder.labels.color")}</label>
                  <div className="eb-color-row">
                    <input type="color" className="eb-color-input" value={embed.color} onChange={(e) => updateEmbed({ color: e.target.value })} />
                    <input type="text" className="eb-input" value={embed.color} placeholder="#5865F2" onChange={(e) => updateEmbed({ color: e.target.value })} />
                  </div>
                </div>

                {/* Autor */}
                <div className="eb-field-group">
                  <span className="eb-group-title">{t("embed_builder.labels.author")}</span>
                  <input
                    className="eb-input"
                    placeholder={t("embed_builder.placeholders.author_name")}
                    value={embed.author_name}
                    onChange={(e) => updateEmbed({ author_name: e.target.value })}
                  />
                  <input
                    className="eb-input"
                    placeholder={t("embed_builder.placeholders.author_url")}
                    value={embed.author_url}
                    onChange={(e) => updateEmbed({ author_url: e.target.value })}
                  />
                  <input
                    className="eb-input"
                    placeholder={t("embed_builder.placeholders.author_icon_url")}
                    value={embed.author_icon_url}
                    onChange={(e) => updateEmbed({ author_icon_url: e.target.value })}
                  />
                </div>

                {/* Título */}
                <div className="eb-field-group">
                  <span className="eb-group-title">{t("embed_builder.labels.title")}</span>
                  <input className="eb-input" placeholder={t("embed_builder.placeholders.title")} value={embed.title} onChange={(e) => updateEmbed({ title: e.target.value })} />
                  <input
                    className="eb-input"
                    placeholder={t("embed_builder.placeholders.title_url")}
                    value={embed.title_url}
                    onChange={(e) => updateEmbed({ title_url: e.target.value })}
                  />
                </div>

                {/* Descrição */}
                <div className="eb-field-group">
                  <span className="eb-group-title">{t("embed_builder.labels.description")}</span>
                  <textarea
                    className="eb-textarea"
                    rows={3}
                    placeholder={t("embed_builder.placeholders.description")}
                    value={embed.description}
                    onChange={(e) => updateEmbed({ description: e.target.value })}
                  />
                </div>

                {/* Fields */}
                <div className="eb-field-group">
                  <span className="eb-group-title">{t("embed_builder.labels.fields")}</span>
                  {embed.fields.map((f, i) => (
                    <div key={i} className="eb-field-item">
                      <div className="eb-field-item-header">
                        <span>{t("embed_builder.labels.field_item", { index: i + 1 })}</span>
                        <label className="eb-inline-label">
                          <input type="checkbox" checked={f.inline} onChange={(e) => updateField(i, { inline: e.target.checked })} />
                          {t("embed_builder.labels.inline")}
                        </label>
                        <button className="eb-remove-btn" onClick={() => removeField(i)}>
                          ✕
                        </button>
                      </div>
                      <input
                        className="eb-input"
                        placeholder={t("embed_builder.placeholders.field_name")}
                        value={f.name}
                        onChange={(e) => updateField(i, { name: e.target.value })}
                      />
                      <textarea
                        className="eb-textarea"
                        rows={2}
                        placeholder={t("embed_builder.placeholders.field_value")}
                        value={f.value}
                        onChange={(e) => updateField(i, { value: e.target.value })}
                      />
                    </div>
                  ))}
                  <button className="eb-add-field-btn" onClick={addField}>
                    {t("embed_builder.labels.add_field")}
                  </button>
                </div>

                {/* Imagens */}
                <div className="eb-field-group">
                  <span className="eb-group-title">{t("embed_builder.labels.images")}</span>
                  <input
                    className="eb-input"
                    placeholder={t("embed_builder.placeholders.image_url")}
                    value={embed.image_url}
                    onChange={(e) => updateEmbed({ image_url: e.target.value })}
                  />
                  <input
                    className="eb-input"
                    placeholder={t("embed_builder.placeholders.thumbnail_url")}
                    value={embed.thumbnail_url}
                    onChange={(e) => updateEmbed({ thumbnail_url: e.target.value })}
                  />
                </div>

                {/* Rodapé */}
                <div className="eb-field-group">
                  <span className="eb-group-title">{t("embed_builder.labels.footer")}</span>
                  <input
                    className="eb-input"
                    placeholder={t("embed_builder.placeholders.footer_text")}
                    value={embed.footer_text}
                    onChange={(e) => updateEmbed({ footer_text: e.target.value })}
                  />
                  <input
                    className="eb-input"
                    placeholder={t("embed_builder.placeholders.footer_icon_url")}
                    value={embed.footer_icon_url}
                    onChange={(e) => updateEmbed({ footer_icon_url: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════ PREVIEW ═══════════════ */}
        {activeTab === "preview" && (
          <div className="eb-preview">
            <div className="eb-discord-preview">
              {/* Mensagem de texto */}
              {content && <div className="eb-preview-content">{preview(content)}</div>}

              {/* Embed */}
              {embed.enabled && (
                <div className="eb-discord-embed" style={{ borderLeftColor: embed.color }}>
                  {/* Autor */}
                  {embed.author_name && (
                    <div className="eb-embed-author">
                      {embed.author_icon_url && <img src={embed.author_icon_url} alt="" className="eb-embed-author-icon" />}
                      <span>{embed.author_name}</span>
                    </div>
                  )}

                  {/* Título */}
                  {embed.title && <div className="eb-embed-title">{preview(embed.title)}</div>}

                  {/* Descrição */}
                  {embed.description && <div className="eb-embed-description">{preview(embed.description)}</div>}

                  {/* Fields */}
                  {embed.fields.length > 0 && (
                    <div className="eb-embed-fields-preview">
                      {embed.fields.map((f, i) => (
                        <div key={i} className={`eb-embed-field ${f.inline ? "inline" : ""}`}>
                          <div className="eb-embed-field-name">{f.name}</div>
                          <div className="eb-embed-field-value">{preview(f.value)}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Imagem grande */}
                  {embed.image_url && <img src={embed.image_url} alt="embed" className="eb-embed-image" />}

                  {/* Thumbnail */}
                  {embed.thumbnail_url && <img src={embed.thumbnail_url} alt="thumb" className="eb-embed-thumbnail" />}

                  {/* Rodapé */}
                  {embed.footer_text && (
                    <div className="eb-embed-footer">
                      {embed.footer_icon_url && <img src={embed.footer_icon_url} alt="" className="eb-embed-footer-icon" />}
                      <span>{preview(embed.footer_text)}</span>
                    </div>
                  )}
                </div>
              )}

              {!content && !embed.enabled && <div className="eb-preview-empty">{t("embed_builder.placeholders.empty_preview")}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmbedBuilder;
